/*
<purpose>One reading of "the suggestion row moved on under the clerk" (Part C, 2026-09-07):
the sentence every surface says about it, and the single predicate that recognises the fact in both
shapes it arrives in.</purpose>
<dependencies>None. Pure — no React, no network, no imports, so any layer (API client, context,
component, page) can key to it without a cycle.</dependencies>
<constraints>
  - A stale row is NOT an error. Its remedy is a RELOAD the surface performs itself, so it never
    reaches the red banner and never asks the clerk to try again — which would only lose the race
    a second time.
  - The two shapes are equally real and neither is preferred: a raw axios 409 whose body carries
    core's sentence (the segments PATCH, the render endpoint), and a result envelope the API client
    already flagged (`stale: true` — a claim's stale_rejected 200, or a 409 it mapped). ONE
    predicate for both, because a surface that recognised only one of them would be right about
    half its callers.
</constraints>
*/

/** What the clerk is told: what happened, and that the remedy already ran. */
export const STALE_SUGGESTION_TOAST = 'Der Vorschlag wurde inzwischen aktualisiert — Ansicht neu geladen';

// The fragment of core's German 409 body ("Dieser Entwurf ist nicht mehr der aktuelle. Bitte das
// Ticket neu laden …", TicketQueryFunctions/SecondaryDraftFunctions). Matched on the SENTENCE
// rather than on the machine code beside it because the same handler shape answers 409 for
// unrelated conflicts too, and only this one means "reload".
const STALE_BODY_FRAGMENT = 'nicht mehr der aktuelle';

/**
 * True for the two shapes a lost suggestion race reaches the FE in: an already-flagged result
 * envelope (`stale`) or an axios 409 whose body says the draft moved on. Anything else — another
 * conflict, the same words on another status, a network error, nothing at all — is false.
 */
export function isStaleSuggestionError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;

  // Shape 1: the API client already read the answer and flagged it.
  if ((err as { stale?: unknown }).stale === true) return true;

  // Shape 2: the raw axios error, for a caller that catches before the client maps it.
  const response = (err as { response?: { status?: number; data?: unknown } }).response;
  if (!response || response.status !== 409) return false;
  const data = response.data;
  const body = typeof data === 'string'
    ? data
    : typeof (data as { message?: unknown })?.message === 'string'
      ? (data as { message: string }).message
      : '';
  return body.includes(STALE_BODY_FRAGMENT);
}
